import type { AggregationType } from '../types';

/**
 * Agrupa un array de datos por un campo clave y aplica una función de agregación
 * sobre uno o más campos numéricos. Si aggregation es 'none' o undefined,
 * retorna los datos sin modificar (backward compat).
 *
 * @param data - Array de registros planos del proxy (ya con field mapping aplicado)
 * @param groupByField - Campo por el que agrupar (Eje X / Categoría)
 * @param valueFields - Campo(s) numérico(s) a agregar (Eje Y, series)
 * @param aggregation - Función a aplicar
 * @returns Array agrupado listo para Recharts
 */
export function aggregateChartData(
  data: Record<string, unknown>[],
  groupByField: string,
  valueFields: string[],
  aggregation?: AggregationType
): Record<string, unknown>[] {
  if (!aggregation || aggregation === 'none' || data.length === 0) return data;

  // Agrupar por el campo X
  const groups = new Map<string, Record<string, unknown>[]>();
  for (const row of data) {
    const key = String(row[groupByField] ?? 'N/A');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  // Aplicar agregación a cada grupo
  const result: Record<string, unknown>[] = [];
  for (const [groupKey, rows] of groups) {
    const aggregated: Record<string, unknown> = { [groupByField]: groupKey };

    for (const field of valueFields) {
      const values = rows
        .map(r => r[field])
        .filter((v): v is number => typeof v === 'number');

      aggregated[field] = applyAggregation(values, aggregation);
    }

    result.push(aggregated);
  }

  return result;
}

function applyAggregation(values: number[], type: AggregationType): number {
  if (values.length === 0) return 0;
  switch (type) {
    case 'sum':     return values.reduce((a, b) => a + b, 0);
    case 'average': return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
    case 'count':   return values.length;
    case 'min':     return Math.min(...values);
    case 'max':     return Math.max(...values);
    case 'last':    return values[values.length - 1];
    case 'first':   return values[0];
    case 'none':    return values[0]; // sin agregación: primer valor del grupo
    default:        return values[0];
  }
}
